import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FolderOpen, ChevronRight, BarChart3, Plus, FileDown, Shield } from 'lucide-react';
import { projectsApi, Project, usersApi } from '../lib/api';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';
import { ExportKPIModal } from '../components/ExportKPIModal';
import { useLogger } from '../utils/logger';

export function ProjectsPage() {
  const logger = useLogger('ProjectsPage');
  const navigate = useNavigate();
  const { hapticFeedback, authReady, hasInitData, isTelegramWebApp, webApp } = useTelegram();
  
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  useEffect(() => {
    logger.componentMount();
    return () => {
      logger.componentUnmount();
    };
  }, []);

  const { data: projects, isLoading, error, refetch } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      logger.apiCall('GET', '/projects');
      try {
        const data = await projectsApi.list();
        logger.apiSuccess('GET', '/projects', data);
        return data;
      } catch (error: any) {
        logger.apiError('GET', '/projects', error);
        throw error;
      }
    },
    enabled: authReady,
    retry: (failureCount, error: any) => {
      // Не повторяем при ошибках авторизации
      if (error?.status === 401 || error?.status === 403) {
        logger.warn('Retry skipped due to auth error', { status: error?.status });
        return false;
      }
      logger.debug(`Retry attempt ${failureCount + 1}`, { failureCount });
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { data: currentUser } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: async () => {
      logger.apiCall('GET', '/users/me');
      try {
        const data = await usersApi.me();
        logger.apiSuccess('GET', '/users/me', data);
        return data;
      } catch (error: any) {
        logger.apiError('GET', '/users/me', error);
        throw error;
      }
    },
    enabled: authReady,
  });

  const isSystemAdmin = currentUser?.isSystemAdmin ?? false;
  
  useEffect(() => {
    logger.debug('User data updated', { 
      isSystemAdmin, 
      userId: currentUser?.id,
      hasProjects: !!projects,
      projectsCount: projects?.length 
    });
  }, [isSystemAdmin, currentUser, projects]);

  const handleProjectClick = (project: Project) => {
    logger.userAction('project_click', { projectId: project.id, projectName: project.name });
    hapticFeedback('light');
    navigate(`/projects/${project.id}/protocols`);
  };

  const handleKpiClick = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    logger.userAction('kpi_click', { projectId });
    hapticFeedback('light');
    navigate(`/projects/${projectId}/kpi`);
  };

  const handleExportClick = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    logger.userAction('export_click', { projectId: project.id, projectName: project.name });
    hapticFeedback('medium');
    setSelectedProject(project);
    setExportModalOpen(true);
  };

  useEffect(() => {
    logger.render('start', { isLoading, hasError: !!error, projectsCount: projects?.length });
  }, [isLoading, error, projects]);

  if (!authReady && isTelegramWebApp) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!authReady && !isTelegramWebApp) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-4">
          <h1 className="text-xl font-bold">Протокол задач</h1>
        </div>
        <div className="p-4 text-center space-y-4">
          <FolderOpen className="w-12 h-12 mx-auto text-gray-300" />
          <p className="text-red-500 font-medium">Откройте через Telegram</p>
          <p className="text-gray-500 text-sm">
            Приложение работает только внутри Telegram, не по прямой ссылке в браузере.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
            <p className="text-sm text-blue-800 font-medium mb-2">Инструкция для Анастасии и других пользователей:</p>
            <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
              <li>Откройте Telegram на телефоне</li>
              <li>Найдите бота <b>@eiplan_bot</b> (ПРОТОКОЛЫ-ЧЕК-ЛИСТЫ)</li>
              <li>Напишите боту в личные сообщения: <b>/start</b></li>
              <li>Нажмите синюю кнопку <b>«📱 Открыть приложение»</b></li>
            </ol>
            <p className="text-xs text-blue-600 mt-3">
              Не открывайте ссылку 24vecfort.ru из группового чата — так приложение не авторизуется.
            </p>
          </div>
          <Button onClick={() => window.open('https://t.me/eiplan_bot', '_blank')} className="w-full">
            Открыть бота @eiplan_bot
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    logger.debug('Rendering loading state');
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    logger.error('Rendering error state', error instanceof Error ? error : undefined, {
      status: (error as any)?.status,
      message: (error as any)?.message,
    });
    const isAuthError = (error as any)?.status === 401;
    const openedWithoutInitData = isTelegramWebApp && !hasInitData;
    
    return (
      <div className="min-h-screen bg-gray-100">
        {/* Синий заголовок */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold">Протокол задач</h1>
            <div className="flex items-center gap-2">
              {isSystemAdmin && (
                <button 
                  onClick={() => {
                    logger.userAction('system_admins_click');
                    hapticFeedback('light');
                    navigate('/system-admins');
                  }}
                  className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                  title="Управление системными администраторами"
                >
                  <Shield className="w-5 h-5" />
                </button>
              )}
              <button 
                onClick={() => {
                  logger.userAction('create_button_click');
                  navigate('/create');
                }}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                title="Создать"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 text-center space-y-4">
          <div className="py-8">
            <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            {!isTelegramWebApp || isAuthError || openedWithoutInitData ? (
              <>
                <p className="text-red-500 mb-2 font-medium">
                  {openedWithoutInitData ? 'Приложение открыто не через бота' : 'Требуется авторизация'}
                </p>
                <p className="text-gray-500 text-sm mb-6">
                  {openedWithoutInitData
                    ? 'Telegram не передал данные входа. Так бывает, если открыть ссылку из группы или браузера.'
                    : 'Откройте приложение через кнопку в личных сообщениях с ботом.'}
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
                  <p className="text-sm text-blue-800 font-medium mb-2">Пошаговая инструкция:</p>
                  <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
                    <li>Закройте это окно (крестик вверху)</li>
                    <li>Откройте бота <b>@eiplan_bot</b> в личных сообщениях</li>
                    <li>Отправьте команду <b>/start</b></li>
                    <li>Нажмите <b>«📱 Открыть приложение»</b> — не ссылку из группы</li>
                  </ol>
                  {webApp?.platform && (
                    <p className="text-xs text-gray-500 mt-3">Клиент: {webApp.platform}</p>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className="text-red-500 mb-2 font-medium">Не удалось загрузить проекты</p>
                <p className="text-gray-500 text-sm mb-2">
                  {(error as any)?.message || 'Проверьте подключение к интернету и попробуйте снова'}
                </p>
                {(error as any)?.status && (
                  <p className="text-xs text-gray-400 mb-6">
                    Код ошибки: {(error as any).status}
                  </p>
                )}
              </>
            )}
            <div className="flex flex-col gap-2">
                     <Button 
                     onClick={() => {
                       logger.userAction('retry_click');
                       hapticFeedback('light');
                       refetch();
                     }}
                     className="w-full"
                   >
                     Повторить
                   </Button>
              {isTelegramWebApp && !isAuthError && (
                <Button 
                  onClick={() => {
                    hapticFeedback('light');
                    navigate('/create');
                  }}
                  variant="secondary"
                  className="w-full"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Создать проект
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Синий заголовок */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Протокол задач</h1>
          <div className="flex items-center gap-2">
            {isSystemAdmin && (
              <button 
                onClick={() => {
                  hapticFeedback('light');
                  navigate('/system-admins');
                }}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                title="Управление системными администраторами"
              >
                <Shield className="w-5 h-5" />
              </button>
            )}
            <button 
              onClick={() => navigate('/create')}
              className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Карточка с проектами */}
        <Card className="overflow-hidden">
          <CardHeader>
            <span className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              Проекты
            </span>
          </CardHeader>
          <CardContent noPadding>
            {projects && projects.length === 0 ? (
              <div className="text-center py-8">
                <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 mb-4">У вас пока нет проектов</p>
                <Button onClick={() => navigate('/create')}>
                  Создать первый проект
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {projects?.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => handleProjectClick(project)}
                    className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {project.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {project.protocolsCount} протокол{getProtocolEnding(project.protocolsCount)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3" onClick={(e) => e.stopPropagation()}>
                      {['ADMIN', 'MANAGER'].includes(project.role) && (
                        <>
                          <button
                            onClick={(e) => handleKpiClick(e, project.id)}
                            className="p-2 rounded-lg hover:bg-blue-50 transition-colors"
                            title="KPI Dashboard"
                          >
                            <BarChart3 className="w-5 h-5 text-blue-500" />
                          </button>
                          <button
                            onClick={(e) => handleExportClick(e, project)}
                            className="p-2 rounded-lg hover:bg-green-50 transition-colors"
                            title="Экспорт KPI в Google Sheets"
                          >
                            <FileDown className="w-5 h-5 text-green-600" />
                          </button>
                        </>
                      )}
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Кнопка добавления проекта */}
        <Button 
          onClick={() => {
            hapticFeedback('light');
            navigate('/create');
          }}
          className="w-full btn-primary"
        >
          <Plus className="w-5 h-5 mr-2" />
          {projects && projects.length > 0 ? 'Добавить проект' : 'Создать проект'}
        </Button>
      </div>

      {/* Модальное окно экспорта */}
      {exportModalOpen && selectedProject && (
        <ExportKPIModal
          projectId={selectedProject.id}
          projectName={selectedProject.name}
          googleSheetId={selectedProject.googleSheetId}
          onClose={() => {
            setExportModalOpen(false);
            setSelectedProject(null);
          }}
        />
      )}
    </div>
  );
}

function getProtocolEnding(count: number): string {
  if (count % 10 === 1 && count % 100 !== 11) return '';
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'а';
  return 'ов';
}
